import { useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Mail, ShieldAlert } from "lucide-react";

export default function OnHold() {
  const { logout, user, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  // Redirect to dashboard if user becomes verified
  useEffect(() => {
    if (!isLoading && user && user.verified) {
      setLocation("/");
    }
  }, [user, isLoading, setLocation]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950/30 dark:bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-amber-100 dark:bg-amber-900 rounded-full flex items-center justify-center">
            <Clock className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <CardTitle className="text-2xl">Account Pending Verification</CardTitle>
          <CardDescription className="mt-2">
            Your account is not yet verified
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-amber-50 dark:bg-amber-950/30 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex gap-3">
              <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <p className="font-medium mb-1">What does this mean?</p>
                <p>Your account has been created but requires verification by an administrator or manager before you can access the application.</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Next Steps:</h4>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex gap-2">
                <span className="text-primary">1.</span>
                Contact your manager or team lead
              </li>
              <li className="flex gap-2">
                <span className="text-primary">2.</span>
                Ask them to verify your account in the admin panel
              </li>
              <li className="flex gap-2">
                <span className="text-primary">3.</span>
                Once verified, refresh this page to access the application
              </li>
            </ul>
          </div>

          {user && (
            <div className="bg-gray-100 dark:bg-gray-900/30 dark:bg-gray-800 rounded-lg p-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Mail className="w-4 h-4" />
                <span>Signed in as: <strong className="text-gray-900 dark:text-gray-100">{user.email}</strong></span>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
            <Button 
              variant="destructive" 
              className="flex-1"
              onClick={logout}
            >
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
